<template>
  <div class="tongxin-layout">
    <TongxinHeader />
    <div class="tongxin-layout__content">
      <div v-if="clinicIdData.isLoading" class="loading-overlay">
        <b-spinner style="border-width: 2px" />
      </div>
      <router-view v-else :key="$route.name" />
    </div>
    <TermPopup v-if="!isGuest" />
    <ResultModal ref="resultModal" />
  </div>
</template>

<!-- this landing page currently serves as single source of entrace for tongxin related pages -->

<script>
import TongxinHeader from '@/components/web/proj/tongxin/layout/Header.vue'
import TermPopup from '@/components/web/proj/tongxin/TermPopup.vue'
import ResultModal from '@/components/web/proj/tongxin/ResultModal.vue'
import tongxinApi from '@/apis/web/tongxin'

export default {
  name: "Layout",
  components: {
    TongxinHeader,
    TermPopup,
    ResultModal
  },
  data() {
    return {
      clinicIdData: {
        value: null,
        isLoading: false
      },
      clinicMetaData: {
        clinicCode: null,
        clinicName: ''
      },
    }
  },
  provide() {
    return {
      clinicIdData: this.clinicIdData,
      clinicMetaData: this.clinicMetaData,
      showSuccessModal: (title, message) => {
        if (this.$refs.resultModal) {
          this.$refs.resultModal.show({ isSuccess: true, title, message, errorReason: '' })
        }
      },
      showErrorModal: (title, errorReason = '') => {
        if (this.$refs.resultModal) {
          this.$refs.resultModal.show({ isSuccess: false, title, message: '', errorReason })
        }
      },
    }
  },
  created() {
    this.initializeSourceType()
    this.initializeClinicId()
    // 從 localStorage 還原後清除，避免下次進入時誤用舊值
    this.clearStoredRedirectParams()
  },
  computed: {
    isGuest() {
      return this.$store.getters['web/tongxin/isGuest']
    }
  },
  watch: {
    '$route.query.sourceType': {
      handler(newSourceType, oldSourceType) {
        if (newSourceType && newSourceType !== oldSourceType) {
          this.initializeSourceType()
        }
      }
    },
    '$route.query.clinicCode': {
      handler(newClinicCode, oldClinicCode) {
        if (newClinicCode && newClinicCode !== oldClinicCode) {
          this.initializeClinicId()
        }
      }
    },
    '$route.query.clinicId': {
      handler(newClinicId, oldClinicId) {
        if (newClinicId && newClinicId !== oldClinicId) {
          this.initializeClinicId()
        }
      }
    }
  },
  methods: {
    /**
     * 從 query 或 liff.state 取得參數
     * LINE LIFF 會把 clinicCode、sourceType 等編碼在 liff.state 裡
     * 例如 liff.state=/step/exam-selection?clinicCode=CPW&sourceType=vaccine
     */
    getQueryParam(key) {
      // 優先從 query 直接取
      if (this.$route.query[key]) {
        return this.$route.query[key]
      }
      // 其次從 liff.state 解析
      const liffState = this.$route.query['liff.state']
      if (liffState) {
        try {
          const parsed = new URL(liffState, 'http://placeholder')
          const value = parsed.searchParams.get(key)
          if (value) return value
        } catch (e) {
          // fall through
        }
      }
      // 最後從 localStorage 還原（註冊轉跳時保存的值）
      return this.getStoredRedirectParam(key)
    },
    getStoredRedirectParam(key) {
      try {
        const raw = localStorage.getItem('tongxin_redirect_params')
        if (!raw) return null
        const data = JSON.parse(raw)
        if (Date.now() > data.expiresAt) {
          localStorage.removeItem('tongxin_redirect_params')
          return null
        }
        return data[key] || null
      } catch (e) {
        return null
      }
    },
    clearStoredRedirectParams() {
      localStorage.removeItem('tongxin_redirect_params')
    },
    initializeSourceType() {
      const querySourceType = this.getQueryParam('sourceType')
      if (querySourceType) {
        this.$store.dispatch('web/tongxin/setSourceType', querySourceType)
      }
    },
    async initializeClinicId() {
      const queryClinicCode = this.getQueryParam('clinicCode')
      const queryClinicId = this.getQueryParam('clinicId')
      const storeClinicId = this.$store.getters['web/tongxin/clinicId']
      const storeClinicCode = this.$store.getters['web/tongxin/clinicCode']

      try {
        this.clinicIdData.isLoading = true

        // 優先順序 1: query.clinicCode → 轉換為 clinicId
        if (queryClinicCode) {
          this.$store.dispatch('web/tongxin/setClinicCode', queryClinicCode)
          const clinic = await this.fetchClinicByCode(queryClinicCode)
          this.clinicIdData.value = clinic.clinicId
          this.clinicMetaData.clinicCode = clinic.clinicCode || queryClinicCode
          this.clinicMetaData.clinicName = clinic.clinicName || ''
          this.$store.dispatch('web/tongxin/setClinicId', clinic.clinicId)
          return
        }

        // 優先順序 2: query.clinicId (向後相容)
        if (queryClinicId) {
          this.clinicIdData.value = queryClinicId
          if (queryClinicCode || storeClinicCode) {
            this.clinicMetaData.clinicCode = queryClinicCode || storeClinicCode || null
          }
          this.$store.dispatch('web/tongxin/setClinicId', queryClinicId)
          return
        }

        // 優先順序 3: store 中已存的 clinicId
        if (storeClinicId) {
          this.clinicIdData.value = storeClinicId
          if (storeClinicCode) {
            this.clinicMetaData.clinicCode = storeClinicCode
          }
          return
        }

        console.warn('Layout.vue: Neither clinicCode nor clinicId found')
      } catch (error) {
        console.error('Failed to initialize clinicId:', error)
      } finally {
        this.clinicIdData.isLoading = false
      }
    },

    /**
     * 透過 clinicCode 查詢診所資訊
     */
    async fetchClinicByCode(clinicCode) {
      const response = await tongxinApi.getClinicByCode(clinicCode)
      const clinics = response.data?.clinics || []

      if (clinics.length === 0) {
        throw new Error(`找不到診所代碼：${clinicCode}`)
      }

      return clinics[0]
    }
  }
};
</script>

<style lang="scss">
.tongxin-layout {
  * {
    font-family: 'Microsoft JhengHei', '微軟正黑體', sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
  }

  &__content {
    max-width: 1000px;
    margin: 0 auto;
    padding: 20px;
    position: relative;
  }

  .text-green {
    color: #207868 !important;
  }

  .text-purple {
    color: #8b5cf6 !important;
  }

  .text-yellow {
    color: #f59e0b !important;
  }

  .text-red {
    color: #dc2626 !important;
    font-weight: 700;
  }

  .heading-icon {
    width: 60px;
    height: 60px;
    background: #bbb;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    animation: scaleIn 0.5s ease-out;

    @media (min-width: 768px) {
      width: 80px;
      height: 80px;
    }

    & svg {
      width: 30px;
      height: 30px;
      color: #fff;

      @media (min-width: 768px) {
        width: 40px;
        height: 40px;
      }
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.5);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .page-heading {
    font-size: 32px;
    font-weight: bold;
    color: #207868;
    margin: 0 0 32px;
    text-align: center;
    position: relative;

    @media (min-width: 768px) {
      margin-bottom: 64px;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 100%;
      height: 3px;
      background: linear-gradient(90deg, #207868, #2a9d8f);
      border-radius: 2px;
    }
  }

  .title-with-underline {
    margin-bottom: 40px;
    text-align: center;
    font-size: 24px;
    font-weight: bold;
    color: #207868;
    position: relative;

    @media (min-width: 768px) {
      font-size: 28px;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -12px;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 3px;
      background: #2a9d8f;
      border-radius: 2px;
    }
  }

  .reminder-grayscale {
    margin-bottom: 24px;
    border-radius: 12px;
    padding: 16px;
    background: #f8f9fa;
    line-height: 1.8;
  }

  .reminder-green {
    margin-bottom: 24px;
    border: 1px solid #2a9d8f;
    border-radius: 8px;
    padding: 16px;
    background: #e8f5f3;
    color: #207868;
    font-size: 16px;
    line-height: 1.8;
  }

  .btn {
    padding: 12px 20px;
    font-size: 16px;
    border: none;
    border-radius: 60px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    @media (min-width: 425px) {
      padding: 18px 30px;
      font-size: 24px;
    }

    @media (min-width: 768px) {
      max-width: 300px;
    }

    &:disabled {
      background: #e9ecef;
      color: #999;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }

    &:not(:disabled):hover {
      transform: translateY(-3px);
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg,transparent,rgba(255,255,255,.2),transparent);
      transition: left .5s;
    }

    &:not(:disabled):hover::before {
      left: 100%;
    }

    &.btn-secondary {
      background: #f8f9fa;
      color: #666;
      border: 2px solid #e9ecef;

      &:not(:disabled):hover {
        background: #e9ecef;
        color: #495057;
      }
    }

    &.btn-primary {
      background: linear-gradient(135deg, #164d43 0%, #207868 100%);
      color: white;

      &:not(:disabled):hover {
        box-shadow: 0 5px 15px rgba(32, 120, 104, 0.3);
      }
    }

    &.btn-danger {
      background: linear-gradient(135deg,#dc2626 0%,#ef4444 100%);
      color: #fff;
      box-shadow: 0 5px 20px rgba(220,38,38,.3);

      &:not(:disabled):hover {
        background: linear-gradient(135deg,#b91c1c 0%,#dc2626 100%);
        box-shadow: 0 8px 25px rgba(220,38,38,.4);
      }
    }

    &.btn-block {
      width: 100%;
      max-width: 100%;
      display: block;
    }
  } // end of .btn

  .fixed-footer-navigation {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    background-color: white;
    border-top: 1px solid #e9ecef;
    box-shadow: 0 -5px 20px rgba(0,0,0,.1);
  }

  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
  }
}
</style>
